import React, { createRef } from "react";
import { type Room, RoomEvent } from "matrix-js-sdk/src/matrix";
import { KnownMembership } from "matrix-js-sdk/src/types";
import classNames from "classnames";
// import type { Call } from "../../../models/Call";
import { RovingTabIndexWrapper } from "../../../accessibility/RovingTabIndex";
import AccessibleButton, { type ButtonEvent } from "../../views/elements/AccessibleButton";
import defaultDispatcher from "../../../dispatcher/dispatcher";
import { Action } from "../../../dispatcher/actions";
import { _t } from "../../../languageHandler";
import { ChevronFace, ContextMenuTooltipButton, type MenuProps } from "../../structures/ContextMenu";
import { DefaultTagID, type TagID } from "../../../stores/room-list-v3/skip-list/tag";
import { type MessagePreview, MessagePreviewStore } from "../../../stores/message-preview/MessagePreviewStore";
import DecoratedRoomAvatar from "../avatars/DecoratedRoomAvatar";
import { RoomNotifState } from "../../../RoomNotifs";
import { MatrixClientPeg } from "../../../MatrixClientPeg";
import { RoomNotificationContextMenu } from "../context_menus/RoomNotificationContextMenu";
import NotificationBadge from "./NotificationBadge";
import { type ActionPayload } from "../../../dispatcher/payloads";
import { RoomNotificationStateStore } from "../../../stores/notifications/RoomNotificationStateStore";
import { type NotificationState, NotificationStateEvents } from "../../../stores/notifications/NotificationState";
import { EchoChamber } from "../../../stores/local-echo/EchoChamber";
import { CachedRoomKey, type RoomEchoChamber } from "../../../stores/local-echo/RoomEchoChamber";
import { PROPERTY_UPDATED } from "../../../stores/local-echo/GenericEchoChamber";
// import PosthogTrackers from "../../../PosthogTrackers";
import { type ViewRoomPayload } from "../../../dispatcher/payloads/ViewRoomPayload";
import { KeyBindingAction } from "../../../accessibility/KeyboardShortcuts";
import { getKeyBindingsManager } from "../../../KeyBindingsManager";
import { RoomGeneralContextMenu } from "../context_menus/RoomGeneralContextMenu";
// import { CallStore, CallStoreEvent } from "../../../stores/CallStore";
import { SdkContextClass } from "../../../contexts/SDKContext";
import { RoomTileSubtitle } from "./RoomTileSubtitle";
import { shouldShowComponent } from "../../../customisations/helpers/UIComponents";
import { UIComponent } from "../../../settings/UIFeature";
import { isKnockDenied } from "../../../utils/membership";
import SettingsStore from "../../../settings/SettingsStore";
import { getContactFormatOrDefault, getContactNameFormat, timestampToDate } from "../../../dth/utils";
import { formatHumanDateOnly, formatHumanTime } from "../../../DateUtils";
import * as TimezoneHandler from "../../../TimezoneHandler";
import { CallRoomMetadata } from "../../../stores/call/useCallStore";
import { Icon as LiveIcon } from "../../../../res/img/element-icons/call/live.svg";
import { CONTACT_FORMAT_CAN_CHANGE_DISPLAY_NAME_IN_CHAT } from "../../../dth/constants";
import { ContactStateManager } from "../../../customisations/ContactStateManager";
import { ContactFormatEnum } from "../../../dth/ContactFormatEnum";
import { DthSessionStorage } from "../../../dth/clientHttp/DthSessionStorage";

interface Props {
    room: Room;
    showMessagePreview: boolean;
    isMinimized: boolean;
    tag: TagID;

    callRoom: CallRoomMetadata | null;
    inCall: boolean;

}

type PartialDOMRect = Pick<DOMRect, "left" | "bottom">;

interface State {
    selected: boolean;
    notificationsMenuPosition: PartialDOMRect | null;
    generalMenuPosition: PartialDOMRect | null;
    // call: Call | null;
    messagePreview: MessagePreview | null;
    isTwelveHour?: boolean;
    userTimezone?: string;

    callRoom: CallRoomMetadata | null;
    inCall: boolean;
    contactFormat: ContactFormatEnum | null;
}

const messagePreviewId = (roomId: string): string => `mx_RoomTile_messagePreview_${roomId}`;

export const contextMenuBelow = (elementRect: PartialDOMRect): MenuProps => {
    // align the context menu's icons with the icon which opened the context menu
    const left = elementRect.left + window.scrollX - 9;
    const top = elementRect.bottom + window.scrollY + 17;
    const chevronFace = ChevronFace.None;
    return { left, top, chevronFace };
};

class RoomTile extends React.PureComponent<Props, State> {
    private dispatcherRef?: string;
    private roomTileRef = createRef<HTMLDivElement>();
    private notificationState: NotificationState;
    private roomProps: RoomEchoChamber;
    private settingWatchers: string[] = [];


    public constructor(props: Props) {
        super(props);

        this.state = {
            selected: SdkContextClass.instance.roomViewStore.getRoomId() === this.props.room.roomId,
            notificationsMenuPosition: null,
            generalMenuPosition: null,
            // call: CallStore.instance.getCall(this.props.room.roomId),
            // generatePreview() will return nothing if the user has previews disabled
            messagePreview: null,
            inCall: this.props.inCall,
            callRoom: this.props.callRoom,
            contactFormat: getContactFormatOrDefault(),
            isTwelveHour: SettingsStore.getValue("showTwelveHourTimestamps"),
            userTimezone: SettingsStore.getValue(TimezoneHandler.USER_TIMEZONE_KEY)
        };

        this.notificationState = RoomNotificationStateStore.instance.getRoomState(this.props.room);
        this.roomProps = EchoChamber.forRoom(this.props.room);
    }

    private onRoomNameUpdate = (room: Room): void => {
        this.forceUpdate();
    };

    private onNotificationUpdate = (): void => {
        this.forceUpdate(); // notification state changed - update
    };

    private onRoomPropertyUpdate = (property: CachedRoomKey): void => {
        if (property === CachedRoomKey.NotificationVolume) this.onNotificationUpdate();
        // else ignore - not important for this tile
    };

    private get showContextMenu(): boolean {
        return (
            this.props.tag !== DefaultTagID.Invite &&
            this.props.room.getMyMembership() !== KnownMembership.Knock &&
            !isKnockDenied(this.props.room) &&
            shouldShowComponent(UIComponent.RoomOptionsMenu)
        );
    }

    private get showMessagePreview(): boolean {
        return !this.props.isMinimized && this.props.showMessagePreview;
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        // console.debug("[RoomTile] componentDidUpdate for room", this.props.room.roomId);

        const showMessageChanged = prevProps.showMessagePreview !== this.props.showMessagePreview;
        const minimizedChanged = prevProps.isMinimized !== this.props.isMinimized;
        if (showMessageChanged || minimizedChanged) {
            this.generatePreview();
        }
        if (prevProps.room?.roomId !== this.props.room?.roomId) {
            MessagePreviewStore.instance.off(
                MessagePreviewStore.getPreviewChangedEventName(prevProps.room),
                this.onRoomPreviewChanged,
            );
            MessagePreviewStore.instance.on(
                MessagePreviewStore.getPreviewChangedEventName(this.props.room),
                this.onRoomPreviewChanged,
            );
            prevProps.room?.off(RoomEvent.Name, this.onRoomNameUpdate);
            this.props.room?.on(RoomEvent.Name, this.onRoomNameUpdate);
        }

        // Aggiorna lo stato se cambiano callRoom o inCall
        if (
            prevProps.callRoom !== this.props.callRoom ||
            prevProps.inCall !== this.props.inCall
        ) {
            this.setState({
                callRoom: this.props.callRoom,
                inCall: this.props.inCall,
            });
        }
    }

    public componentDidMount(): void {
        // console.debug("[RoomTile] Mounting RoomTile for room", this.props.room.roomId);

        this.generatePreview();

        // when we're first rendered (or our sublist is expanded) make sure we are visible if we're active
        if (this.state.selected) {
            this.scrollIntoView();
        }

        SdkContextClass.instance.roomViewStore.addRoomListener(this.props.room.roomId, this.onActiveRoomUpdate);
        this.dispatcherRef = defaultDispatcher.register(this.onAction);
        MessagePreviewStore.instance.on(
            MessagePreviewStore.getPreviewChangedEventName(this.props.room),
            this.onRoomPreviewChanged,
        );
        this.notificationState.on(NotificationStateEvents.Update, this.onNotificationUpdate);
        this.roomProps.on(PROPERTY_UPDATED, this.onRoomPropertyUpdate);
        this.props.room.on(RoomEvent.Name, this.onRoomNameUpdate);
        // CallStore.instance.on(CallStoreEvent.Call, this.onCallChanged);

        // Recalculate the call for this room, since it could've changed between
        // construction and mounting
        // this.setState({ call: CallStore.instance.getCall(this.props.room.roomId) });

        this.settingWatchers = [
            SettingsStore.watchSetting("showTwelveHourTimestamps", null, (...[, , , value]) =>
                this.setState({ isTwelveHour: value as boolean }),
            ),
            SettingsStore.watchSetting(TimezoneHandler.USER_TIMEZONE_KEY, null, (...[, , , value]) =>
                this.setState({ userTimezone: value as string }),
            ),
            SettingsStore.watchSetting("contact_format", null, (...[, , , , newVal]) =>
                this.setState({ contactFormat: newVal as ContactFormatEnum }),
            ),
        ];
    }

    public componentWillUnmount(): void {
        // console.debug("[RoomTile] Unmounting RoomTile for room", this.props.room.roomId);

        SdkContextClass.instance.roomViewStore.removeRoomListener(this.props.room.roomId, this.onActiveRoomUpdate);
        MessagePreviewStore.instance.off(
            MessagePreviewStore.getPreviewChangedEventName(this.props.room),
            this.onRoomPreviewChanged,
        );
        this.props.room.off(RoomEvent.Name, this.onRoomNameUpdate);
        defaultDispatcher.unregister(this.dispatcherRef);
        this.notificationState.off(NotificationStateEvents.Update, this.onNotificationUpdate);
        this.roomProps.off(PROPERTY_UPDATED, this.onRoomPropertyUpdate);
        // CallStore.instance.off(CallStoreEvent.Call, this.onCallChanged);

        for (const watcher of this.settingWatchers) {
            SettingsStore.unwatchSetting(watcher);
        }

    }

    private onAction = (payload: ActionPayload): void => {
        if (
            payload.action === Action.ViewRoom &&
            payload.room_id === this.props.room.roomId &&
            payload.show_room_tile
        ) {
            setTimeout(() => {
                this.scrollIntoView();
            });
        }
    };

    private onRoomPreviewChanged = (room: Room): void => {
        if (this.props.room && room.roomId === this.props.room.roomId) {
            this.generatePreview();
        }
    };

    // private onCallChanged = (call: Call, roomId: string): void => {
    //     if (roomId === this.props.room?.roomId) this.setState({ call });
    // };

    private async generatePreview(): Promise<void> {
        if (!this.showMessagePreview) {
            return;
        }

        const messagePreview =
            (await MessagePreviewStore.instance.getPreviewForRoom(this.props.room, this.props.tag)) ?? null;
        this.setState({ messagePreview });
    }

    private scrollIntoView = (): void => {
        if (!this.roomTileRef.current) return;
        this.roomTileRef.current.scrollIntoView({
            block: "nearest",
            behavior: "auto",
        });
    };

    private onTileClick = async (ev: ButtonEvent): Promise<void> => {
        ev.preventDefault();
        ev.stopPropagation();

        const action = getKeyBindingsManager().getAccessibilityAction(ev as React.KeyboardEvent);
        const clearSearch = ([KeyBindingAction.Enter, KeyBindingAction.Space] as Array<string | undefined>).includes(
            action,
        );

        defaultDispatcher.dispatch<ViewRoomPayload>({
            action: Action.ViewRoom,
            show_room_tile: true, // make sure the room is visible in the list
            room_id: this.props.room.roomId,
            clear_search: clearSearch,
            metricsTrigger: "RoomList",
            metricsViaKeyboard: ev.type !== "click",
        });

        // Forza la perdita del focus dopo il click per evitare hover bloccato
        if (ev.currentTarget && typeof (ev.currentTarget as HTMLElement).blur === "function") {
            (ev.currentTarget as HTMLElement).blur();
        }

    };

    private onActiveRoomUpdate = (isActive: boolean): void => {
        this.setState({ selected: isActive });
    };

    private onNotificationsMenuOpenClick = (ev: ButtonEvent): void => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = ev.target as HTMLButtonElement;
        this.setState({ notificationsMenuPosition: target.getBoundingClientRect() });

        // PosthogTrackers.trackInteraction("WebRoomListRoomTileNotificationsMenu", ev);
    };

    private onCloseNotificationsMenu = (): void => {
        this.setState({ notificationsMenuPosition: null });
    };

    private onGeneralMenuOpenClick = (ev: ButtonEvent): void => {
        ev.preventDefault();
        ev.stopPropagation();
        const target = ev.target as HTMLButtonElement;
        this.setState({ generalMenuPosition: target.getBoundingClientRect() });
    };

    private onContextMenu = (ev: React.MouseEvent): void => {
        // If we don't have a context menu to show, ignore the action.
        if (!this.showContextMenu) return;

        ev.preventDefault();
        ev.stopPropagation();
        this.setState({
            generalMenuPosition: {
                left: ev.clientX,
                bottom: ev.clientY,
            },
        });
    };

    private onCloseGeneralMenu = (): void => {
        this.setState({ generalMenuPosition: null });
    };

    private renderNotificationsMenu(isActive: boolean): React.ReactElement | null {
        if (
            MatrixClientPeg.safeGet().isGuest() ||
            this.props.tag === DefaultTagID.Archived ||
            !this.showContextMenu ||
            this.props.isMinimized
        ) {
            // the menu makes no sense in these cases so do not show one
            return null;
        }

        const state = this.roomProps.notificationVolume;

        const privateConfiguration = DthSessionStorage.getPrivateConfiguration();
        const enableMentions = privateConfiguration?.allowMentions ?? false;

        const classes = classNames("mx_RoomTile_notificationsButton", {
            // Show bell icon for the default case too.
            mx_RoomNotificationContextMenu_iconBell: state === RoomNotifState.AllMessages,
            mx_RoomNotificationContextMenu_iconBellDot: state === RoomNotifState.AllMessagesLoud,
            mx_RoomNotificationContextMenu_iconBellMentions: enableMentions && state === RoomNotifState.MentionsOnly,
            mx_RoomNotificationContextMenu_iconBellCrossed: state === RoomNotifState.Mute,

            // Only show the icon by default if the room is overridden to muted.
            // TODO: [FTUE Notifications] Probably need to detect global mute state
            mx_RoomTile_notificationsButton_show: state === RoomNotifState.Mute,
        });

        return (
            <React.Fragment>
                <ContextMenuTooltipButton
                    className={classes}
                    onClick={this.onNotificationsMenuOpenClick}
                    title={_t("room_list.notification_options")}
                    isExpanded={!!this.state.notificationsMenuPosition}
                    tabIndex={isActive ? 0 : -1}
                />
                {this.state.notificationsMenuPosition && (
                    <RoomNotificationContextMenu
                        {...contextMenuBelow(this.state.notificationsMenuPosition)}
                        onFinished={this.onCloseNotificationsMenu}
                        room={this.props.room}
                    />
                )}
            </React.Fragment>
        );
    }

    private renderGeneralMenu(): React.ReactElement | null {
        if (!this.showContextMenu) return null; // no menu to show
        return (
            <React.Fragment>
                <ContextMenuTooltipButton
                    className="mx_RoomTile_menuButton"
                    onClick={this.onGeneralMenuOpenClick}
                    title={_t("room.context_menu.title")}
                    isExpanded={!!this.state.generalMenuPosition}
                />
                {this.state.generalMenuPosition && (
                    <RoomGeneralContextMenu
                        {...contextMenuBelow(this.state.generalMenuPosition)}
                        room={this.props.room}
                        // onPostFavoriteClick={(ev: ButtonEvent) =>
                        //     PosthogTrackers.trackInteraction("WebRoomListRoomTileContextMenuFavouriteToggle", ev)
                        // }
                        // onPostInviteClick={(ev: ButtonEvent) =>
                        //     PosthogTrackers.trackInteraction("WebRoomListRoomTileContextMenuInviteItem", ev)
                        // }
                        // onPostSettingsClick={(ev: ButtonEvent) =>
                        //     PosthogTrackers.trackInteraction("WebRoomListRoomTileContextMenuSettingsItem", ev)
                        // }
                        // onPostLeaveClick={(ev: ButtonEvent) =>
                        //     PosthogTrackers.trackInteraction("WebRoomListRoomTileContextMenuLeaveItem", ev)
                        // }
                        // onPostMarkAsReadClick={(ev: ButtonEvent) =>
                        //     PosthogTrackers.trackInteraction("WebRoomListRoomTileContextMenuMarkRead", ev)
                        // }
                        // onPostMarkAsUnreadClick={(ev: ButtonEvent) =>
                        //     PosthogTrackers.trackInteraction("WebRoomListRoomTileContextMenuMarkUnread", ev)
                        // }
                        onFinished={this.onCloseGeneralMenu}
                    />
                )}
            </React.Fragment>
        );
    }

    /**
     * RoomTile has a subtile if one of the following applies:
     * - there is a call
     * - message previews are enabled and there is a previewable message
     */
    private get shouldRenderSubtitle(): boolean {
        return /*!!this.state.call || */(this.props.showMessagePreview && !!this.state.messagePreview);
    }

    public render(): React.ReactElement {
        const classes = classNames({
            mx_RoomTile: true,
            mx_RoomTile_sticky: false,
            // SettingsStore.getValue("feature_ask_to_join") &&
            // (this.props.room.getMyMembership() === KnownMembership.Knock || isKnockDenied(this.props.room)),
            mx_RoomTile_selected: this.state.selected,
            mx_RoomTile_hasMenuOpen: !!(this.state.generalMenuPosition || this.state.notificationsMenuPosition),
            mx_RoomTile_minimized: this.props.isMinimized,
        });

        let name = this.props.room.name;

        if (typeof name !== "string") name = "";
        name = name.replace(":", ":\u200b"); // add a zero-width space to allow linewrapping after the colon

        // Se è una chat diretta e il formato del nome visualizzato può cambiare in base alla configurazione
        if (CONTACT_FORMAT_CAN_CHANGE_DISPLAY_NAME_IN_CHAT) {
            try {
                const members = this.props.room.getMembers();
                // Se è una chat diretta (2 membri e tag direct) e uno dei membri è l'utente stesso, 
                // mostra il nome dell'altro membro formattato secondo la configurazione
                if (this.props.tag == DefaultTagID.DM && members.length <= 2) {
                    const myUserId = MatrixClientPeg.safeGet().getUserId();
                    const otherMember = members.find(m => m.userId !== myUserId);
                    const otherUserId = otherMember?.userId;
                    // Se abbiamo trovato un altro membro, prova a ottenere il suo contatto e mostra il nome formattato
                    if (otherUserId) {
                        const participantContact = ContactStateManager.getContactByMatrixName(otherUserId);
                        const newRoomName: string = getContactNameFormat(participantContact, this.state.contactFormat);
                        if (newRoomName) name = newRoomName;
                    }
                }
            } catch (error) {
                console.warn("Error while formatting contact name in RoomTile:", error);
            }
        }

        // Per la data dell'ultimo messaggio, usiamo il timestamp dell'evento nella preview se disponibile, 
        // altrimenti ricaviamo l'ultimo timestamp visibile dallo store
        const lastLiveTimestamp = this.state.messagePreview?.event.getTs() ?? this.props.room.getLastActiveTimestamp();
        let date: string = "";
        let fullDate: string = "";
        let time: string = "";
        let fullTime: string = "";
        try {
            if (lastLiveTimestamp > 0) {
                const lastDate = timestampToDate(lastLiveTimestamp);

                date = formatHumanDateOnly(lastDate, { useLongName: false });
                time = formatHumanTime(lastDate, { showTwelveHour: this.state.isTwelveHour, showSeconds: false, timeZone: this.state.userTimezone });
                fullTime = formatHumanTime(lastDate, { showTwelveHour: this.state.isTwelveHour, showSeconds: true, timeZone: this.state.userTimezone });
                fullDate = formatHumanDateOnly(lastDate, { useLongName: true, forceFullDate: true });
            }
        } catch {
            date = "";
            fullDate = "";
            time = "";
            fullTime = "";
        }

        let badge: React.ReactNode;
        if (!this.props.isMinimized && this.notificationState) {
            // aria-hidden because we summarise the unread count/highlight status in a manual aria-label below
            badge = (
                <div className="mx_RoomTile_badgeContainer" aria-hidden="true">
                    <NotificationBadge notification={this.notificationState} roomId={this.props.room.roomId} />
                </div>
            );
        }

        let callBadge: React.ReactNode;
        // Mostra il badge di chiamata solo se siamo in chiamata in questa stanza
        if (this.state.inCall &&
            this.state.callRoom &&
            this.state.callRoom.matrixRoom &&
            this.props.room.roomId === this.state.callRoom.matrixRoom.roomId) {
            callBadge = (
                <div className="mx_RoomTile_badgeContainer" aria-hidden="true">
                    <LiveIcon width={28} height={28} className="mx_LiveIcon" />
                </div>
            );
        }

        const subtitle = this.shouldRenderSubtitle ? (
            <RoomTileSubtitle
                // call={this.state.call}
                messagePreview={this.state.messagePreview}
                roomId={this.props.room.roomId}
                showMessagePreview={this.props.showMessagePreview}
            />
        ) : null;

        const titleClasses = classNames({
            mx_RoomTile_title: true,
            mx_RoomTile_titleWithSubtitle: !!subtitle,
            mx_RoomTile_titleHasUnreadEvents: this.notificationState.isUnread,
        });

        const titleContainer = this.props.isMinimized ? null : (
            <div className="mx_RoomTile_titleContainer">
                <div title={name} className={titleClasses} tabIndex={-1}>
                    <span dir="auto">{name}</span>
                </div>
                {subtitle}
            </div>
        );

        const dateContainer = this.props.isMinimized ? null : (
            <div className="mx_RoomTile_dateContainer">
                <div title={fullTime} className="mx_RoomTile_subtitle" tabIndex={-1}>
                    <span dir="auto">{time}</span>
                </div>
                <div className="mx_RoomTile_subtitle" id={`mx_RoomTile_timestamp_${this.props.room.roomId}`} title={fullDate}>
                    <span className="mx_RoomTile_subtitle_text">{date}</span>
                </div>
            </div>
        );

        let ariaLabel = name;
        // The following labels are written in such a fashion to increase screen reader efficiency (speed).
        if (this.props.tag === DefaultTagID.Invite) {
            // append nothing
        } else if (this.notificationState.hasMentions) {
            ariaLabel +=
                " " +
                _t("a11y.n_unread_messages_mentions", {
                    count: this.notificationState.count,
                });
        } else if (this.notificationState.hasUnreadCount) {
            ariaLabel +=
                " " +
                _t("a11y.n_unread_messages", {
                    count: this.notificationState.count,
                });
        } else if (this.notificationState.isUnread) {
            ariaLabel += " " + _t("a11y.unread_messages");
        }

        let ariaDescribedBy: string;
        if (this.showMessagePreview) {
            ariaDescribedBy = messagePreviewId(this.props.room.roomId);
        }

        return (
            <React.Fragment>
                {/* Bridge per aggiornare call dallo store */}
                <RovingTabIndexWrapper inputRef={this.roomTileRef}>
                    {({ onFocus, isActive, ref }) => (
                        <AccessibleButton
                            onFocus={onFocus}
                            tabIndex={isActive ? 0 : -1}
                            ref={ref}
                            className={classes}
                            onClick={this.onTileClick}
                            onContextMenu={this.onContextMenu}
                            role="treeitem"
                            aria-label={ariaLabel}
                            aria-selected={this.state.selected}
                            aria-describedby={ariaDescribedBy}
                            title={this.props.isMinimized && !this.state.generalMenuPosition ? name : undefined}
                        >
                            <DecoratedRoomAvatar
                                room={this.props.room}
                                size="32px"
                                displayBadge={this.props.isMinimized}
                                tooltipProps={{ tabIndex: isActive ? 0 : -1 }}
                            />
                            {titleContainer}
                            {dateContainer}
                            {badge}
                            {callBadge}
                            {this.renderGeneralMenu()}
                            {this.renderNotificationsMenu(isActive)}
                        </AccessibleButton>
                    )}
                </RovingTabIndexWrapper>
            </React.Fragment>
        );
    }
}

export default RoomTile;
